import {createPromiseClient} from "@connectrpc/connect";
import {createConnectTransport} from "@connectrpc/connect-web";
import {
    AddBlacklistRecordRequest,
    AddMagnetUserRequest,
    ApproveContactRequest,
    BlacklistRecord,
    CommonFilter,
    DeleteBlacklistRecordRequest,
    DeleteMagnetUserRequest,
    GetBlacklistPagedRequest,
    GetContactsPagedFilter,
    GetContactsPagedRequest,
    GetMessageRequest,
    GetUsersPagedRequest,
    MagnetUser,
    RedecideContactRequest,
    RejectContactRequest,
    SkipContactRequest,
    UpdateMagnetUserRequest
} from "@alconost/kraken-magnet/src/proto/magnet/v1alpha1/magnet_pb";
import {GetHealthRequest} from "@alconost/kraken-health/src/proto/health/v1alpha1/health_pb";

import {MagnetService} from "@alconost/kraken-magnet/src/proto/magnet/v1alpha1/magnet_connect";
import {HealthService} from "@alconost/kraken-health/src/proto/health/v1alpha1/health_connect";

import {Timestamp as ConnectRpcTimestamp} from "@bufbuild/protobuf";

import {getConnectRpcHeaders} from "./grpsUtils";
import {handleServiceError} from "../helpers/catchErrorUtils";
import {removeTextInBrackets} from "../helpers/utils";

const HEALTH_HOST = 'https://health-n6j2kqa64a-uc.a.run.app/';
const MAGNET_HOST = 'https://magnet-n6j2kqa64a-uc.a.run.app/';

const healthTransport = createConnectTransport({
    baseUrl: HEALTH_HOST,
});
const healthClient = createPromiseClient(HealthService, healthTransport);

const magnetTransport = createConnectTransport({
    baseUrl: MAGNET_HOST,
});
const magnetClient = createPromiseClient(MagnetService, magnetTransport);

// health
export const getServiceHealthRequest = async (token, service) => {
    const headers = getConnectRpcHeaders(token);
    const getHealthRequest = new GetHealthRequest();

    getHealthRequest.service = service;

    try {
        const stream = healthClient.getHealth(getHealthRequest, headers);
        let healthResults = [];
        
        for await (const res of stream) {
            if (res.health) {
                healthResults.push(res.health);
            }
        }
        
        return healthResults;
    } catch (err) {
        handleServiceError(0, err.message);
        return [];
    }
}

// magnet
export const getUsersPagedRequest = async (token, limit, offset, search, account, order, sortField) => {
    const headers = getConnectRpcHeaders(token);
    const getUsersPagedRequest = new GetUsersPagedRequest();
    const commonFilter = new CommonFilter();

    getUsersPagedRequest.limit = limit;
    getUsersPagedRequest.offset = offset;
    if (order) {
        getUsersPagedRequest.order = order;
    }
    if (sortField) {
        getUsersPagedRequest.sortField = sortField;
    }

    commonFilter.account = account;
    if (search) {
        commonFilter.search = search;
    }

    getUsersPagedRequest.filter = commonFilter;

    return magnetClient.getUsersPaged(getUsersPagedRequest, headers).then(res => res).catch(err => {
        handleServiceError(0, err.message);
        return null;
    });
}

export const addMargetUserRequest = async (token, id, account) => {
    const headers = getConnectRpcHeaders(token);
    const addMagnetUserRequest = new AddMagnetUserRequest();
    const magnetUser = new MagnetUser();

    magnetUser.id = id;
    magnetUser.account = account;

    addMagnetUserRequest.user = magnetUser;

    return magnetClient.addMagnetUser(addMagnetUserRequest, headers).then(res => res).catch(err => {
        handleServiceError(0, err.message);
        return null;
    });
}

export const deleteMargetUserRequest = async (token, id, account) => {
    const headers = getConnectRpcHeaders(token);
    const deleteMagnetUserRequest = new DeleteMagnetUserRequest();

    deleteMagnetUserRequest.id = id;
    deleteMagnetUserRequest.account = account;

    return magnetClient.deleteMagnetUser(deleteMagnetUserRequest, headers).then(res => res).catch(err => {
        handleServiceError(0, err.message);
        return null;
    });
}

export const updateMargetUserRequest = async (token, item) => {
    const headers = getConnectRpcHeaders(token);
    const updateMagnetUserRequest = new UpdateMagnetUserRequest();
    const magnetUser = new MagnetUser();

    const {id, account, checkpoint, isInbound, isOutbound, status, parseResult} = item;
    magnetUser.id = id;
    magnetUser.account = account;

    const timestamp = new ConnectRpcTimestamp();
    timestamp.seconds = checkpoint.seconds;
    timestamp.nanos = checkpoint.nanos;
    magnetUser.checkpoint = timestamp;
    magnetUser.isInbound = isInbound;
    magnetUser.isOutbound = isOutbound;
    magnetUser.status = status;
    magnetUser.parseResult = parseResult;

    updateMagnetUserRequest.user = magnetUser;

    return magnetClient.updateMagnetUser(updateMagnetUserRequest, headers).then(res => res).catch(err => {
        handleServiceError(0, err.message);
        return null;
    });
}

export const addDomainBlacklistRecordRequest = async (token, value, account) => {
    const headers = getConnectRpcHeaders(token);
    const addDomainBlacklistRecordRequest = new AddBlacklistRecordRequest();

    addDomainBlacklistRecordRequest.record = value;
    addDomainBlacklistRecordRequest.account = account;

    return magnetClient.addBlacklistRecord(addDomainBlacklistRecordRequest, headers).then(res => res).catch(err => {
        handleServiceError(0, err.message);
        return null;
    });
}

export const deleteDomainBlacklistRecordRequest = async (token, id, value, account) => {
    const headers = getConnectRpcHeaders(token);
    const deleteBlacklistRecordRequest = new DeleteBlacklistRecordRequest();
    const domainBlacklistRecord = new BlacklistRecord();

    domainBlacklistRecord.id = id;
    domainBlacklistRecord.record = value;
    domainBlacklistRecord.account = account;

    deleteBlacklistRecordRequest.record = domainBlacklistRecord;

    return magnetClient.deleteBlacklistRecord(deleteBlacklistRecordRequest, headers).then(res => res).catch(err => {
        handleServiceError(0, err.message);
        return null;
    });
}

export const getDomainBlacklistPagedRequest = async (token, limit, offset, search, account) => {
    const headers = getConnectRpcHeaders(token);
    const getBlacklistPagedRequest = new GetBlacklistPagedRequest();
    const commonFilter = new CommonFilter();

    getBlacklistPagedRequest.limit = limit;
    getBlacklistPagedRequest.offset = offset;
    commonFilter.search = search;
    commonFilter.account = account;

    getBlacklistPagedRequest.filter = commonFilter;

    return magnetClient.getBlacklistPaged(getBlacklistPagedRequest, headers).then(res => res).catch(err => {
        handleServiceError(0, err.message);
        return null;
    });
}

export const getContactsPagedRequest = async (token, limit, offset, search, account, status, order) => {
    const headers = getConnectRpcHeaders(token);
    const getContactsPagedRequest = new GetContactsPagedRequest();
    const getContactsPagedFilter = new GetContactsPagedFilter();

    getContactsPagedFilter.search = search;
    getContactsPagedFilter.account = account;

    if (status && status !== 'All') {
         getContactsPagedFilter.status = status;
    }
    getContactsPagedRequest.order = order;
    getContactsPagedRequest.limit = limit;
    getContactsPagedRequest.offset = offset;
    getContactsPagedRequest.filter = getContactsPagedFilter;

    return magnetClient.getContactsPaged(getContactsPagedRequest, headers).then(res => res).catch(err => {
        handleServiceError(0, err.message);
        return null;
    });
}

export const updateMagnetContactRequest = async (token, account, contact) => {
    const headers = getConnectRpcHeaders(token);
    const redecideContactRequest = new RedecideContactRequest();

    redecideContactRequest.account = account;
    redecideContactRequest.email = contact.id;

    return magnetClient.redecideContact(redecideContactRequest, headers).then(res => res).catch(err => {
        handleServiceError(0, err.message);
        return null;
    });
}

export const getMagnetMessageRequest = async (token, account) => {
    const headers = getConnectRpcHeaders(token);
    const getMessageRequest = new GetMessageRequest();

    getMessageRequest.account = account;

    const stream = magnetClient.getMessage(getMessageRequest, headers);
    let response = [];

    for await (const res of stream) {
        if (res?.result?.case !== "processing") {
            response.push(res);
        }
    }

    return response;
}

export const approveContactInCloseRequest = async (token, account, email) => {
    const headers = getConnectRpcHeaders(token);
    const approveContactRequest = new ApproveContactRequest();

    approveContactRequest.account = account;
    approveContactRequest.email = email;


    return magnetClient.approveContact(approveContactRequest, headers).then(res => {
        return res;
    }).catch(err => {
        if (typeof err === "object" && err.message) {
            handleServiceError(0, err.message);
        }
        else {
            return removeTextInBrackets(err);
        }
    });
}

export const rejectMagnetContactRequest = async (token, account, email) => {
    const headers = getConnectRpcHeaders(token);
    const rejectContactRequest = new RejectContactRequest();

    rejectContactRequest.account = account;
    rejectContactRequest.email = email;

    return magnetClient.rejectContact(rejectContactRequest, headers).then(res => res).catch(err => {
        handleServiceError(0, err.message);
        return null;
    });
}

export const skipMagnetContactRequest = async (token, account, email) => {
    const headers = getConnectRpcHeaders(token);
    const skipContactRequest = new SkipContactRequest();

    skipContactRequest.account = account;
    skipContactRequest.email = email;

    return magnetClient.skipContact(skipContactRequest, headers).then(res => res).catch(err => {
        handleServiceError(0, err.message);
        return null;
    });
}
